import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import { Tag } from "../../components/Tag/Tag";

import { useToast } from "../../context/ToastContext";
import { getColorBaseTokenValue } from "../../design-tokens";

import ProductionApi, { OccupancyTimelineResponse, TimelineInterval, TimelineMode } from "../../services/ProductionApi";
import StationStateApi from "../../services/StationStateApi";
import { normalizeStationState, Vaga } from "../../services/stationState.types";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import useWorkSchedule, { useShiftList } from "../../hooks/useWorkSchedule";
import ShiftFilter from "../../components/ShiftFilter/ShiftFilter";
import { formatDurationUnits } from "../../utilities/duration";
import { useCoalescedActivityTracking } from "../../hooks/useActivityTracking";
import { ActivityEventType } from "../../utilities/activityLog";
import {
  OFF_SHIFT_STATE,
  OFF_SHIFT_PROD_STATE,
  OFF_SHIFT_PROD_COLOR,
  isProducingState,
  isOffShift,
  splitIntervalByShift,
  formatLocalTime,
  shiftWindowLabel,
} from "../../utilities/workSchedule";
import TimelineRibbon, { RibbonSegment } from "./TimelineRibbon";
import styles from "./ReportTimeline.module.css";

// Modo estação inclui dois estados sintéticos derivados da janela do turno (espelho do backend):
// "fora de turno" (cinza) e "produção fora de turno" (produzindo fora do turno → destacado).
// Modo vaga permanece sem essa lente.
const STATION_STATES = ["Produzindo", "CicloExcedido", "Ausencia", "FaltaMaterial", OFF_SHIFT_PROD_STATE, OFF_SHIFT_STATE];
const VAGA_STATES = ["Ocupada", "Livre"];

const pad = (n: number): string => String(n).padStart(2, "0");
const toYmd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};
const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const ReportTimeline: React.FC = () => {
  const { t, i18n } = useTranslation("timeline");
  const { displayToast } = useToast();
  // Coalescido: clicar várias vezes na seta de dia é UMA intenção, não várias.
  const trackDay = useCoalescedActivityTracking();
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";
  // Jornada completa para o sombreado fora-de-turno em "Todos". Com um turno filtrado, o backend
  // devolve só a janela do turno (tudo dentro dele → sem cinza/fora-de-turno).
  const schedule = useWorkSchedule();
  const shiftList = useShiftList();
  const [shiftFilter, setShiftFilter] = useState<string | null>(null);

  const [mode, setMode] = useState<TimelineMode>("station");
  const [vagaKey, setVagaKey] = useState<string>("");
  const [period, setPeriod] = useState<"live" | "history">("live");
  const [histDay, setHistDay] = useState<Date>(() => addDays(startOfDay(new Date()), -1));
  const [selected, setSelected] = useState<Set<string>>(() => new Set(STATION_STATES));

  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [data, setData] = useState<OccupancyTimelineResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<string>("");

  // Calculado a fresco no uso (não congela no mount → não fica obsoleto ao virar a meia-noite).
  const yesterdayNow = (): Date => addDays(startOfDay(new Date()), -1);

  // "2D 10h 17m" / "10h 17m" / "17m" / "42s" — a partir de 24h ganha o prefixo de
  // dias em vez de somar horas indefinidamente (ver `utilities/duration.ts`).
  const fmtDur = useCallback((secs: number): string => formatDurationUnits(secs), []);
  // Fuso FIXO da POC (−3) para que os rótulos batam com as fronteiras do turno e a banda cinza,
  // independentemente do fuso do browser (as fatias são cortadas em 08:00/17:00 local −3).
  const fmtTime = useCallback(
    (iso: string): string => formatLocalTime(iso, locale, schedule.tzOffsetHours),
    [locale, schedule.tzOffsetHours],
  );
  const fmtDayLabel = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" }),
    [locale],
  );

  // Lista de vagas (para o seletor do modo vaga) — uma leitura do estado atual da estação.
  useEffect(() => {
    let alive = true;
    StationStateApi.GetStationState()
      .then((res: any) => {
        if (!alive) return;
        const st = normalizeStationState(res?.data);
        const vs = (st?.vagas ?? []).filter((v) => v.type != null).sort((a, b) => a.vagaKey.localeCompare(b.vagaKey));
        setVagas(vs);
        if (vs.length > 0) setVagaKey((prev) => prev || vs.find((v) => v.type === "Operacao")?.vagaKey || vs[0].vagaKey);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const reqIdRef = useRef(0);
  const load = useCallback(
    async (): Promise<void> => {
      // Modo vaga sem vaga resolvida: não busca (evita disparar uma requisição de ESTAÇÃO sob o modo vaga).
      if (mode === "vaga" && !vagaKey) {
        setData(null);
        setLoading(false);
        return;
      }
      const myId = ++reqIdRef.current; // sequencial: só a resposta mais recente comita
      try {
        const params: { from?: string; to?: string; vaga?: string; shiftId?: string } = {};
        if (mode === "vaga" && vagaKey) params.vaga = vagaKey;
        if (period === "history") {
          const ymd = toYmd(histDay);
          params.from = ymd; // sempre AMBAS as datas (evita o bug de só-`to` → 400)
          params.to = ymd;
        }
        // Filtro de turno só no modo estação: o backend recorta a janela ao início/fim do turno.
        if (mode === "station" && shiftFilter) params.shiftId = shiftFilter;
        const resp = await ProductionApi.GetOccupancyTimeline(Object.keys(params).length ? params : undefined);
        if (myId !== reqIdRef.current) return; // resposta obsoleta (troca de alvo/dia): descarta
        setData((resp?.data as OccupancyTimelineResponse) ?? null);
        setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
      } catch (err: any) {
        if (myId !== reqIdRef.current) return;
        displayToast("error", t("loadErrorTitle"), err?.errors?.[0] ?? t("loadErrorMsg"), false, 6000);
      } finally {
        if (myId === reqIdRef.current) setLoading(false);
      }
    },
    [mode, vagaKey, period, histDay, shiftFilter, displayToast, t, locale],
  );

  // Recarrega ao mudar alvo/vaga/período/dia/turno (load muda de identidade com esses filtros).
  useEffect(() => {
    void load();
  }, [load]);
  // Tempo real via SignalR só na janela "ao vivo" (histórico não muda).
  useLiveRefresh(() => void load(), period === "live");

  // Ao trocar de modo, reseta os estados selecionados para todos do modo.
  useEffect(() => {
    setSelected(new Set(mode === "station" ? STATION_STATES : VAGA_STATES));
  }, [mode]);

  const stateColor = useMemo(
    (): Record<string, string> => ({
      Produzindo: getColorBaseTokenValue("color.feedback.success.500"),
      CicloExcedido: getColorBaseTokenValue("color.feedback.warning.700"), // Amarelo 700 — distinto de "Ausência"
      Ausencia: "#FF0000", // exibido como "Parada"
      FaltaMaterial: getColorBaseTokenValue("color.feedback.warning.500"), // Amarelo 500 — exibido como "Ausência"
      Ocupada: getColorBaseTokenValue("color.secondary.500"),
      Livre: getColorBaseTokenValue("color.neutral.300"),
      [OFF_SHIFT_STATE]: getColorBaseTokenValue("color.neutral.500"), // Cinza 500 — exibido como "NPP"
      // Violeta forte, não usado por nenhum outro estado → salta aos olhos na fita.
      [OFF_SHIFT_PROD_STATE]: OFF_SHIFT_PROD_COLOR,
    }),
    [],
  );
  const stateLabel = useCallback(
    (s: string): string =>
      ({
        Produzindo: t("stProduzindo"),
        CicloExcedido: t("stExcedido"),
        Ausencia: t("stAusencia"),
        FaltaMaterial: t("stMaterial"),
        Ocupada: t("stOcupada"),
        Livre: t("stLivre"),
        [OFF_SHIFT_STATE]: t("stForaTurno"),
        [OFF_SHIFT_PROD_STATE]: t("stProducaoForaTurno"),
      })[s] ?? s,
    [t],
  );

  const modeStates = mode === "station" ? STATION_STATES : VAGA_STATES;

  const derived = useMemo(() => {
    const intervals: TimelineInterval[] = data?.intervals ?? [];
    const hasData = !!data?.hasData && intervals.length > 0;

    // Fatia cada intervalo nas fronteiras do turno (modo estação). Fora de turno vira o estado
    // sintético cinza; dentro de turno preserva o estado real. Modo vaga não recebe a lente.
    type Slice = { state: string; origState: string; startedAt: string; seconds: number; offShift: boolean; startMs: number };

    // Regra "produção fora de turno" = ENTRADA E SAÍDA do carro fora do turno. Um carro presente é
    // um ciclo ocupado → Produzindo (entrada) seguido de CicloExcedido; a saída é quando a produção
    // termina (próximo intervalo). Um carro que entrou DENTRO do turno e ficou parado até o próximo
    // turno (CicloExcedido) NÃO é produção fora de turno — vira cinza comum. Só conta como produção
    // fora de turno se entrada E saída forem ambas fora do turno e observadas na janela (uma entrada
    // cortada pela janela — CicloExcedido sem Produzindo — não qualifica: entrada desconhecida).
    const episodeOffShiftProd = new Array<boolean>(intervals.length).fill(false);
    if (mode === "station") {
      let i = 0;
      while (i < intervals.length) {
        if (!isProducingState(intervals[i].state)) { i++; continue; }
        const entryKnown = intervals[i].state === "Produzindo"; // CicloExcedido solto = entrada cortada
        let j = i + 1;
        while (j < intervals.length && intervals[j].state === "CicloExcedido") j++; // novo Produzindo = outro carro
        const exitKnown = j < intervals.length; // há intervalo após → saída observada (carro saiu/trocou)
        let qualifies = false;
        if (entryKnown && exitKnown) {
          qualifies =
            isOffShift(new Date(intervals[i].startedAt), schedule) &&
            isOffShift(new Date(intervals[j].startedAt), schedule);
        }
        for (let k = i; k < j; k++) episodeOffShiftProd[k] = qualifies;
        i = j;
      }
    }

    const allSlices: Slice[] = [];
    intervals.forEach((iv, idx) => {
      if (mode === "station") {
        for (const sl of splitIntervalByShift(iv.startedAt, iv.seconds, schedule)) {
          // Fora de turno: produção genuína fora de turno (entrada E saída fora) → violeta;
          // senão (inclui carro parado do turno anterior) → cinza. Dentro do turno = estado real.
          const sliceState = !sl.offShift
            ? iv.state
            : isProducingState(iv.state) && episodeOffShiftProd[idx]
              ? OFF_SHIFT_PROD_STATE
              : OFF_SHIFT_STATE;
          allSlices.push({
            state: sliceState,
            origState: iv.state,
            startedAt: sl.startedAt,
            seconds: sl.seconds,
            offShift: sl.offShift,
            startMs: new Date(sl.startedAt).getTime(),
          });
        }
      } else {
        allSlices.push({ state: iv.state, origState: iv.state, startedAt: iv.startedAt, seconds: iv.seconds, offShift: false, startMs: new Date(iv.startedAt).getTime() });
      }
    });

    // Eixo de relógio real: janela = do início da 1ª fatia ao fim da última (fatias são contíguas).
    const windowStart = allSlices.length ? allSlices[0].startMs : 0;
    const last = allSlices[allSlices.length - 1];
    const windowEnd = last ? last.startMs + last.seconds * 1000 : 0;
    const span = Math.max(1, windowEnd - windowStart);
    const windowSeconds = span / 1000;

    const visible = allSlices.filter((sl) => selected.has(sl.state));
    const segments: RibbonSegment[] = visible.map((sl) => ({
      state: sl.state,
      startPct: ((sl.startMs - windowStart) / span) * 100,
      widthPct: ((sl.seconds * 1000) / span) * 100,
      startedAt: sl.startedAt,
      seconds: sl.seconds,
    }));

    // KPIs estação recomputados SÓ sobre o tempo dentro do turno (Q2: números que o front consegue).
    // Em paralelo, soma a PRODUÇÃO fora de turno (o que a operação precisa identificar).
    let inTotal = 0, inProducing = 0, inAbsence = 0, longestAbsence = 0, offShiftProducing = 0;
    if (mode === "station") {
      for (const sl of allSlices) {
        if (sl.offShift) {
          // Só produção fora de turno QUALIFICADA (entrada+saída fora) entra no total do banner.
          if (sl.state === OFF_SHIFT_PROD_STATE) offShiftProducing += sl.seconds;
          continue;
        }
        inTotal += sl.seconds;
        if (isProducingState(sl.origState)) inProducing += sl.seconds;
        if (sl.origState === "Ausencia") {
          inAbsence += sl.seconds;
          if (sl.seconds > longestAbsence) longestAbsence = sl.seconds;
        }
      }
    }
    const shiftKpis =
      mode === "station" && inTotal > 0
        ? {
            availabilityPct: (inProducing / inTotal) * 100,
            producingSeconds: inProducing,
            absenceSeconds: inAbsence,
            longestStopSeconds: longestAbsence,
          }
        : null;

    // KPIs modo vaga: derivados dos intervalos (backend manda kpis=null nesse modo).
    const occSec = intervals.filter((iv) => iv.state === "Ocupada").reduce((a, iv) => a + iv.seconds, 0);
    const freeSec = intervals.filter((iv) => iv.state === "Livre").reduce((a, iv) => a + iv.seconds, 0);
    const vagaTotal = occSec + freeSec;

    return { intervals, hasData, visible, windowSeconds, segments, shiftKpis, offShiftProducing, occSec, freeSec, vagaTotal };
  }, [data, selected, mode, schedule]);

  const toggleState = (s: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const goHistDay = (delta: number): void => {
    const next = addDays(histDay, delta);
    if (next.getTime() > yesterdayNow().getTime()) return; // hoje é "ao vivo"
    setHistDay(next);
    trackDay(ActivityEventType.DayChange, { day: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}` });
  };

  const vagaOptions = useMemo(() => {
    const order: Array<{ type: string; labelKey: string }> = [
      { type: "Operacao", labelKey: "elevator" },
      { type: "Supermercado", labelKey: "supermarket" },
      { type: "Extra", labelKey: "extra" },
    ];
    return order.map((g) => ({
      label: t(g.labelKey),
      options: vagas
        .filter((v) => v.type === g.type)
        .map((v, i) => ({ value: v.vagaKey, label: `${t(g.labelKey)} ${i + 1}` })),
    })).filter((g) => g.options.length > 0);
  }, [vagas, t]);

  // Disponibilidade e afins recomputados no front sobre o tempo DENTRO do turno (não o backend).
  const kpis = derived.shiftKpis;
  const availGood = (kpis?.availabilityPct ?? 0) >= 75;
  const stopBad = (kpis?.longestStopSeconds ?? 0) >= 20 * 60;

  const windowChip =
    period === "live"
      ? `${t("todayLabel")} · ${t("live")}`
      : fmtDayLabel(histDay);

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <Tag
          label={period === "live" ? t("live") : t("history")}
          severity={period === "live" ? "successInverse" : "secondary"}
          icon={period === "live" ? "sensors" : "history"}
        />
      </PageHeader>

      {/* Filtros */}
      <div className={`${styles.card} ${styles.filters}`}>
        <div className={styles.filterGroup}>
          <span className={styles.fieldLabel}>{t("fTarget")}</span>
          <div className={styles.segmented} role="group">
            <button type="button" onClick={() => setMode("station")} className={`${styles.segBtn} ${mode === "station" ? styles.segBtnActive : ""}`}>
              <span className="material-symbols-outlined">warehouse</span>
              {t("station")}
            </button>
            <button type="button" onClick={() => setMode("vaga")} disabled={vagas.length === 0} className={`${styles.segBtn} ${mode === "vaga" ? styles.segBtnActive : ""}`}>
              <span className="material-symbols-outlined">event_seat</span>
              {t("vaga")}
            </button>
          </div>
        </div>

        {mode === "vaga" && (
          <div className={styles.filterGroup}>
            <span className={styles.fieldLabel}>{t("vaga")}</span>
            <select className={styles.select} value={vagaKey} onChange={(e) => setVagaKey(e.target.value)}>
              {vagaOptions.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}

        <div className={styles.filterGroup}>
          <span className={styles.fieldLabel}>{t("fPeriod")}</span>
          <div className={styles.periodRow}>
            <div className={styles.segmented} role="group">
              <button type="button" onClick={() => setPeriod("live")} className={`${styles.segBtn} ${period === "live" ? styles.segBtnActive : ""}`}>
                <span className="material-symbols-outlined">sensors</span>
                {t("live")}
              </button>
              <button type="button" onClick={() => setPeriod("history")} className={`${styles.segBtn} ${period === "history" ? styles.segBtnActive : ""}`}>
                <span className="material-symbols-outlined">history</span>
                {t("history")}
              </button>
            </div>
            {period === "history" && (
              <div className={styles.dayStepper}>
                <button type="button" className={styles.dayNav} onClick={() => goHistDay(-1)} aria-label={t("prevDay")}>
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <span className={styles.dayLabel}>
                  <span className="material-symbols-outlined">calendar_month</span>
                  {fmtDayLabel(histDay)}
                </span>
                <button type="button" className={styles.dayNav} onClick={() => goHistDay(1)} disabled={histDay.getTime() >= yesterdayNow().getTime()} aria-label={t("nextDay")}>
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {mode === "station" && (
          <ShiftFilter value={shiftFilter} onChange={setShiftFilter} shifts={shiftList} />
        )}

        <div className={styles.filterSpacer} />
        <span className={styles.windowChip}>
          <span className="material-symbols-outlined">{period === "live" ? "fiber_manual_record" : "calendar_month"}</span>
          {windowChip}
        </span>
      </div>

      {loading && !data ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className={styles.kpiRow}>
            {mode === "station" ? (
              <>
                <div className={styles.kpi}>
                  <div className={styles.kpiHead}>
                    <span className={`material-symbols-outlined ${styles.kpiIcon}`}>check_circle</span>
                    <span className={styles.kpiLabel}>{t("kAvailability")}</span>
                  </div>
                  <div className={styles.kpiValue} style={{ color: availGood ? stateColor.Produzindo : stateColor.FaltaMaterial }}>
                    {kpis ? `${kpis.availabilityPct.toLocaleString(locale, { maximumFractionDigits: 0 })}%` : "—"}
                  </div>
                  <div className={styles.kpiSub}>{t("kAvailabilitySub")}</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiHead}>
                    <span className={`material-symbols-outlined ${styles.kpiIcon}`} style={{ color: stateColor.Produzindo }}>play_circle</span>
                    <span className={styles.kpiLabel}>{t("kProducing")}</span>
                  </div>
                  <div className={styles.kpiValue}>{kpis ? fmtDur(kpis.producingSeconds) : "—"}</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiHead}>
                    <span className={`material-symbols-outlined ${styles.kpiIcon}`} style={{ color: stateColor.Ausencia }}>person_off</span>
                    <span className={styles.kpiLabel}>{t("kAbsence")}</span>
                  </div>
                  <div className={styles.kpiValue}>{kpis ? fmtDur(kpis.absenceSeconds) : "—"}</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiHead}>
                    <span className={`material-symbols-outlined ${styles.kpiIcon}`}>hourglass_bottom</span>
                    <span className={styles.kpiLabel}>{t("kLongest")}</span>
                  </div>
                  <div className={styles.kpiValue} style={{ color: stopBad ? stateColor.FaltaMaterial : undefined }}>
                    {kpis ? fmtDur(kpis.longestStopSeconds) : "—"}
                  </div>
                  <div className={styles.kpiSub}>{t("kLongestSub")}</div>
                </div>
              </>
            ) : (
              <>
                <div className={styles.kpi}>
                  <div className={styles.kpiHead}>
                    <span className={`material-symbols-outlined ${styles.kpiIcon}`} style={{ color: stateColor.Ocupada }}>event_seat</span>
                    <span className={styles.kpiLabel}>{t("kOccupancy")}</span>
                  </div>
                  <div className={styles.kpiValue}>
                    {derived.vagaTotal > 0 ? `${Math.round((derived.occSec / derived.vagaTotal) * 100)}%` : "—"}
                  </div>
                  <div className={styles.kpiSub}>{t("kOccupancySub")}</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiHead}>
                    <span className={`material-symbols-outlined ${styles.kpiIcon}`} style={{ color: stateColor.Ocupada }}>directions_car</span>
                    <span className={styles.kpiLabel}>{t("kOccupied")}</span>
                  </div>
                  <div className={styles.kpiValue}>{fmtDur(derived.occSec)}</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiHead}>
                    <span className={`material-symbols-outlined ${styles.kpiIcon}`}>check_box_outline_blank</span>
                    <span className={styles.kpiLabel}>{t("kFree")}</span>
                  </div>
                  <div className={styles.kpiValue}>{fmtDur(derived.freeSec)}</div>
                </div>
              </>
            )}
          </div>

          {/* Fita */}
          <section className={styles.card}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>
                <span className="material-symbols-outlined">timeline</span>
                {t("ribbonTitle")}
              </span>
              <div className={styles.chips}>
                {modeStates.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`${styles.chip} ${selected.has(s) ? "" : styles.chipOff}`}
                    onClick={() => toggleState(s)}
                    aria-pressed={selected.has(s)}
                  >
                    <span className={styles.chipSwatch} style={{ background: stateColor[s] }} />
                    {stateLabel(s)}
                  </button>
                ))}
              </div>
            </div>
            {mode === "station" && derived.offShiftProducing > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  margin: "0 0 12px",
                  borderRadius: 8,
                  background: `${OFF_SHIFT_PROD_COLOR}18`,
                  color: OFF_SHIFT_PROD_COLOR,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                }}
              >
                <span className="material-symbols-outlined">warning</span>
                {t("offShiftProdAlert", { dur: fmtDur(derived.offShiftProducing) })}
              </div>
            )}
            <div className={styles.ribbonBody}>
              {derived.hasData && derived.segments.length > 0 ? (
                <TimelineRibbon segments={derived.segments} colorOf={(s) => stateColor[s] ?? "#9ca3af"} labelOf={stateLabel} fmtTime={fmtTime} fmtDur={fmtDur} t={t} />
              ) : (
                <div className={styles.empty}>
                  <span className={`material-symbols-outlined ${styles.emptyIcon}`}>timeline</span>
                  <div className={styles.emptySub}>{derived.hasData ? t("emptyFiltered") : t("emptyNoData")}</div>
                </div>
              )}
            </div>
          </section>

          {/* Intervalos */}
          <section className={styles.card}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>
                <span className="material-symbols-outlined">table_rows</span>
                {t("listTitle")}
              </span>
              <span className={styles.count}>{derived.visible.length} {t("intervalsWord")}</span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={`${styles.th} ${styles.thLeft}`}>{t("colStart")}</th>
                    <th className={`${styles.th} ${styles.thLeft}`}>{t("colState")}</th>
                    <th className={styles.th}>{t("colDur")}</th>
                    <th className={styles.th}>{t("colShare")}</th>
                  </tr>
                </thead>
                <tbody>
                  {derived.visible.map((iv, i) => (
                    <tr key={i}>
                      <td className={`${styles.td} ${styles.tdLeft}`}>{fmtTime(iv.startedAt)}</td>
                      <td className={`${styles.td} ${styles.tdLeft}`}>
                        <span className={styles.statePill} style={{ background: `${stateColor[iv.state]}22`, color: stateColor[iv.state] }}>
                          {stateLabel(iv.state)}
                        </span>
                      </td>
                      <td className={styles.td}>{fmtDur(iv.seconds)}</td>
                      <td className={styles.td}>
                        {derived.windowSeconds > 0 ? ((iv.seconds / derived.windowSeconds) * 100).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "0,0"}%
                      </td>
                    </tr>
                  ))}
                  {derived.visible.length === 0 && (
                    <tr>
                      <td className={styles.tdEmpty} colSpan={4}>{derived.hasData ? t("emptyFiltered") : t("emptyNoData")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className={styles.footerNote}>
            {period === "live" ? t("realtimeNote") : t("historyNote")} · {t("updatedAt")} {lastUpdated}
            {mode === "station" && !shiftFilter && <> · {t("shiftNote", { shift: shiftWindowLabel(schedule) })}</>}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportTimeline;
